// frontend/src/components/engineer/pages/EngineerApplyLeavePage.tsx
// Engineer Portal - Apply Leave for Workers (Direct to HR Approval)
// PHOENICIAN TECHNICAL SERVICES LLC - Professional Traditional UI
// NOTE: Annual Leave redirects to EngineerApplyAnnualLeavePage for travel details

import React, { useState, useCallback, useMemo } from 'react';
import { AuthUser } from '../../../services/auth.service';
import { 
  engineerLeaveService, 
  LeaveType, 
  LEAVE_TYPES,
  EmployeeSearchResult,
  LeaveBalance,
  LeaveApplicationData,
  LeaveApplicationRecord
} from '../../../services/engineerLeave.service';
import styles from './EngineerApplyLeavePage.module.css';

interface EngineerApplyLeavePageProps {
  user?: AuthUser;
  onNavigate?: (page: string) => void;
}

type Step = 'search' | 'select-leave' | 'dates' | 'review' | 'success';

const EngineerApplyLeavePage: React.FC<EngineerApplyLeavePageProps> = ({ user, onNavigate }) => {
  // ============================================================================
  // STATE
  // ============================================================================
  
  const [currentStep, setCurrentStep] = useState<Step>('search');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Search State
  const [searchPin, setSearchPin] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Employee State
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearchResult | null>(null);
  const [leaveBalance, setLeaveBalance] = useState<LeaveBalance | null>(null);
  
  // Leave History State
  const [leaveHistory, setLeaveHistory] = useState<LeaveApplicationRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Leave Application State
  const [selectedLeaveType, setSelectedLeaveType] = useState<LeaveType | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isAlreadyTaken, setIsAlreadyTaken] = useState(false);
  const [reason, setReason] = useState('');
  const [engineerRemarks, setEngineerRemarks] = useState('');
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  
  // Success State
  const [submittedApplicationId, setSubmittedApplicationId] = useState<string | null>(null);
  
  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  
  const totalDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return engineerLeaveService.calculateTotalDays(new Date(startDate), new Date(endDate));
  }, [startDate, endDate]);
  
  const isCertificateRequired = useMemo(() => {
    if (!selectedLeaveType) return false;
    return engineerLeaveService.isCertificateRequired(selectedLeaveType, isAlreadyTaken);
  }, [selectedLeaveType, isAlreadyTaken]);
  
  const selectedLeaveTypeInfo = useMemo(() => {
    return LEAVE_TYPES.find(lt => lt.id === selectedLeaveType);
  }, [selectedLeaveType]);
  
  // ============================================================================
  // HANDLERS
  // ============================================================================
  
  // Search Employee
  const handleSearchEmployee = useCallback(async () => {
    if (!searchPin.trim()) {
      setError('Please enter employee PIN/Number');
      return;
    }
    
    setIsSearching(true);
    setError(null);
    setLeaveHistory([]);
    
    try {
      const employee = await engineerLeaveService.searchEmployeeByPin(searchPin.trim());
      
      if (!employee) {
        setError('Employee not found. Please check the PIN/Number.');
        return;
      }
      
      const balance = await engineerLeaveService.getLeaveBalance(employee.employeeId);
      
      setSelectedEmployee(employee);
      setLeaveBalance(balance);
      
      setLoadingHistory(true);
      try {
        const history = await engineerLeaveService.getEmployeeLeaveApplications(employee.employeeId, 5);
        setLeaveHistory(history);
      } catch (err) {
        console.error('Error fetching leave history:', err);
      } finally {
        setLoadingHistory(false);
      }
      
      setCurrentStep('select-leave');
      
    } catch (err: any) {
      setError(err.message || 'Failed to search employee');
    } finally {
      setIsSearching(false);
    }
  }, [searchPin]);
  
  // Select Leave Type
  // 🆕 UPDATED: Redirect to Annual Leave page when Annual Leave is selected
  const handleSelectLeaveType = useCallback((leaveType: LeaveType) => {
    // If Annual Leave is selected, redirect to the dedicated Annual Leave page
    // which has airport search, travel details, passport, signature fields
    if (leaveType === 'annual') {
      onNavigate?.('apply-annual-leave');
      return;
    }
    
    setSelectedLeaveType(leaveType);
    setCurrentStep('dates');
    setStartDate('');
    setEndDate('');
    setIsAlreadyTaken(false);
    setCertificateFile(null);
  }, [onNavigate]);
  
  // Handle Certificate Upload
  const handleCertificateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size too large. Maximum 10MB allowed.');
        return;
      }
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Allowed: PDF, JPG, PNG');
        return;
      }
      setCertificateFile(file);
      setError(null);
    }
  }, []);
  
  // Proceed to Review
  const handleProceedToReview = useCallback(() => {
    if (!startDate || !endDate) {
      setError('Please select start and end dates');
      return;
    }
    
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date cannot be after end date');
      return;
    }
    
    if (isCertificateRequired && !certificateFile) {
      setError('Certificate is required for this leave type');
      return;
    }
    
    if (leaveBalance && selectedLeaveType) {
      const validation = engineerLeaveService.validateLeaveApplication(
        {
          employeeId: selectedEmployee?.employeeId,
          employeeName: selectedEmployee?.employeeName,
          leaveType: selectedLeaveType,
          startDate: new Date(startDate),
          endDate: new Date(endDate),
          totalDays,
          isAlreadyTaken,
          certificateFile: certificateFile || undefined
        },
        leaveBalance
      );
      
      if (!validation.valid) {
        setError(validation.error || 'Validation failed');
        return;
      }
    }
    
    setError(null);
    setCurrentStep('review');
  }, [startDate, endDate, isCertificateRequired, certificateFile, leaveBalance, selectedLeaveType, selectedEmployee, totalDays, isAlreadyTaken]);
  
  // Submit Application
  const handleSubmitApplication = useCallback(async () => {
    if (!selectedEmployee || !selectedLeaveType || !user) {
      setError('Missing required information');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const applicationData: LeaveApplicationData = {
        employeeId: selectedEmployee.employeeId,
        employeeName: selectedEmployee.employeeName,
        employeePin: selectedEmployee.employeePin,
        leaveType: selectedLeaveType,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        totalDays,
        reason: reason || `Leave applied by Engineer on behalf of ${selectedEmployee.employeeName}`,
        isAlreadyTaken,
        certificateFile: certificateFile || undefined,
        engineerRemarks,
        appliedByEngineerId: user.employeeNo || user.uid,
        appliedByEngineerName: user.name || 'Engineer'
      };
      
      const result = await engineerLeaveService.submitLeaveApplication(applicationData);
      
      if (result.success) {
        setSubmittedApplicationId(result.applicationId || null);
        setCurrentStep('success');
      } else {
        setError(result.error || 'Failed to submit application');
      }
      
    } catch (err: any) {
      setError(err.message || 'Failed to submit application');
    } finally {
      setIsLoading(false);
    }
  }, [selectedEmployee, selectedLeaveType, user, startDate, endDate, totalDays, reason, isAlreadyTaken, certificateFile, engineerRemarks]);
  
  // Reset and Start Over
  const handleStartOver = useCallback(() => {
    setCurrentStep('search');
    setSearchPin('');
    setSelectedEmployee(null);
    setLeaveBalance(null);
    setLeaveHistory([]);
    setSelectedLeaveType(null);
    setStartDate('');
    setEndDate('');
    setIsAlreadyTaken(false);
    setReason('');
    setEngineerRemarks('');
    setCertificateFile(null);
    setSubmittedApplicationId(null);
    setError(null);
  }, []);
  
  // Format date helper
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  
  // Get status text
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'pending': return 'Pending';
      default: return 'Pending';
    }
  };
  
  // ============================================================================
  // RENDER STEPS
  // ============================================================================
  
  // Step 1: Search Employee
  const renderSearchStep = () => (
    <div className={styles.stepContainer}>
      <div className={styles.searchCard}>
        <div className={styles.cardHeader}>
          <h2>Employee Search</h2>
          <p>Enter the employee PIN or number to begin leave application</p>
        </div>
        
        <div className={styles.searchInputGroup}>
          <label className={styles.inputLabel}>Employee PIN / Number</label>
          <div className={styles.inputRow}>
            <input
              type="text"
              value={searchPin}
              onChange={(e) => setSearchPin(e.target.value)}
              placeholder="e.g., 1234 or EMP1234"
              className={styles.searchInput}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchEmployee()}
            />
            <button 
              onClick={handleSearchEmployee}
              disabled={isSearching}
              className={styles.primaryButton}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      </div>
      
      <div className={styles.actionLinks}>
        <button onClick={() => onNavigate?.('leave-reports')} className={styles.linkButton}>
          View All Leave Reports
        </button>
      </div>
    </div>
  );
  
  // Step 2: Select Leave Type (WITH LEAVE HISTORY)
  const renderSelectLeaveStep = () => (
    <div className={styles.stepContainer}>
      {/* Employee Info Card */}
      <div className={styles.employeeInfoCard}>
        <div className={styles.cardHeader}>
          <h3>Employee Details</h3>
          <button onClick={handleStartOver} className={styles.textButton}>
            Change Employee
          </button>
        </div>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Name</span>
            <span className={styles.infoValue}>{selectedEmployee?.employeeName}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>PIN</span>
            <span className={styles.infoValue}>{selectedEmployee?.employeePin}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Designation</span>
            <span className={styles.infoValue}>{selectedEmployee?.designation || 'Worker'}</span>
          </div>
          {selectedEmployee?.department && (
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Department</span>
              <span className={styles.infoValue}>{selectedEmployee.department}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Leave History Section */}
      {(leaveHistory.length > 0 || loadingHistory) && (
        <div className={styles.sectionCard}>
          <div className={styles.cardHeader}>
            <h3>Recent Leave Applications</h3>
          </div>
          
          {loadingHistory ? (
            <div className={styles.loadingState}>Loading leave history...</div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Date Applied</th>
                    <th>Leave Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Days</th>
                    <th>Status</th>
                    <th>HR Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveHistory.map((leave) => {
                    const leaveInfo = LEAVE_TYPES.find(lt => lt.id === leave.leaveType);
                    return (
                      <tr key={leave.id}>
                        <td>{formatDate(leave.applicationDate)}</td>
                        <td>{leaveInfo?.displayName || leave.leaveType}</td>
                        <td>{formatDate(leave.startDate)}</td>
                        <td>{formatDate(leave.endDate)}</td>
                        <td className={styles.centerCell}>{leave.totalDays}</td>
                        <td>
                          <span className={`${styles.statusLabel} ${styles[`status${leave.hrApprovalStatus.charAt(0).toUpperCase() + leave.hrApprovalStatus.slice(1)}`]}`}>
                            {getStatusText(leave.hrApprovalStatus)}
                          </span>
                        </td>
                        <td className={styles.remarksCell}>
                          {leave.hrRemarks || (leave.hrApprovalStatus === 'pending' ? 'Awaiting Review' : '-')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Leave Balance */}
      <div className={styles.sectionCard}>
        <div className={styles.cardHeader}>
          <h3>Leave Balance Summary</h3>
        </div>
        <div className={styles.balanceGrid}>
          {LEAVE_TYPES.map((type) => {
            const balance = leaveBalance?.[type.id] || { total: 0, used: 0, pending: 0, remaining: 0 };
            return (
              <div key={type.id} className={styles.balanceItem}>
                <div className={styles.balanceHeader}>{type.displayName}</div>
                <div className={styles.balanceBody}>
                  <div className={styles.balanceMain}>
                    <span className={styles.balanceRemaining}>{balance.remaining}</span>
                    <span className={styles.balanceTotal}>/ {balance.total}</span>
                  </div>
                  <div className={styles.balanceDetails}>
                    <span>Used: {balance.used}</span>
                    {balance.pending > 0 && <span>Pending: {balance.pending}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Select Leave Type */}
      <div className={styles.sectionCard}>
        <div className={styles.cardHeader}>
          <h3>Select Leave Type</h3>
        </div>
        <div className={styles.leaveTypeGrid}>
          {LEAVE_TYPES.map((type) => {
            const balance = leaveBalance?.[type.id] || { remaining: 0 };
            const isDisabled = balance.remaining <= 0;
            const isAnnualLeave = type.id === 'annual';
            
            return (
              <button
                key={type.id}
                onClick={() => !isDisabled && handleSelectLeaveType(type.id)}
                disabled={isDisabled}
                className={`${styles.leaveTypeOption} ${isDisabled ? styles.disabled : ''} ${isAnnualLeave ? styles.annualLeaveHighlight : ''}`}
              >
                <div className={styles.leaveTypeName}>
                  {isAnnualLeave && <span style={{ marginRight: '6px' }}>✈️</span>}
                  {type.displayName}
                </div>
                <div className={styles.leaveTypeDesc}>
                  {isAnnualLeave ? 'Includes travel details & airport selection' : type.description}
                </div>
                <div className={styles.leaveTypeBalance}>
                  {balance.remaining} days available
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
  
  // Step 3: Select Dates
  const renderDatesStep = () => (
    <div className={styles.stepContainer}>
      {/* Mini Employee Info */}
      <div className={styles.miniInfoBar}>
        <span><strong>Employee:</strong> {selectedEmployee?.employeeName}</span>
        <span><strong>Leave Type:</strong> {selectedLeaveTypeInfo?.displayName}</span>
      </div>
      
      {/* Date Selection Form */}
      <div className={styles.formCard}>
        <div className={styles.cardHeader}>
          <h3>Leave Period Details</h3>
        </div>
        
        <div className={styles.formBody}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Start Date <span className={styles.required}>*</span></label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={styles.formInput}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>End Date <span className={styles.required}>*</span></label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className={styles.formInput}
              />
            </div>
          </div>
          
          {totalDays > 0 && (
            <div className={styles.totalDaysDisplay}>
              Total Duration: <strong>{totalDays} day{totalDays > 1 ? 's' : ''}</strong>
            </div>
          )}
          
          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={isAlreadyTaken}
                onChange={(e) => setIsAlreadyTaken(e.target.checked)}
                className={styles.checkbox}
              />
              <span>Leave Already Taken (Past Leave Regularization)</span>
            </label>
            {isAlreadyTaken && (
              <p className={styles.helperText}>
                This option is for regularizing leave that has already been taken.
              </p>
            )}
          </div>
          
          {isCertificateRequired && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Supporting Document <span className={styles.required}>*</span>
              </label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleCertificateChange}
                className={styles.fileInput}
              />
              {certificateFile && (
                <div className={styles.fileInfo}>
                  Selected: {certificateFile.name}
                  <button onClick={() => setCertificateFile(null)} className={styles.removeFileBtn}>Remove</button>
                </div>
              )}
              <p className={styles.helperText}>Accepted formats: PDF, JPG, PNG (Max 10MB)</p>
            </div>
          )}
          
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Reason for Leave</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for leave (optional)"
              rows={3}
              className={styles.formTextarea}
            />
          </div>
        </div>
      </div>
      
      {/* Navigation Buttons */}
      <div className={styles.buttonRow}>
        <button onClick={() => setCurrentStep('select-leave')} className={styles.secondaryButton}>
          Back
        </button>
        <button 
          onClick={handleProceedToReview}
          disabled={!startDate || !endDate}
          className={styles.primaryButton}
        >
          Continue to Review
        </button>
      </div>
    </div>
  );
  
  // Step 4: Review
  const renderReviewStep = () => (
    <div className={styles.stepContainer}>
      <div className={styles.reviewCard}>
        <div className={styles.cardHeader}>
          <h3>Review Leave Application</h3>
        </div>
        
        <div className={styles.reviewSection}>
          <h4>Employee Information</h4>
          <div className={styles.reviewGrid}>
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Name</span>
              <span className={styles.reviewValue}>{selectedEmployee?.employeeName}</span>
            </div>
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>PIN</span>
              <span className={styles.reviewValue}>{selectedEmployee?.employeePin}</span>
            </div>
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Designation</span>
              <span className={styles.reviewValue}>{selectedEmployee?.designation || 'Worker'}</span>
            </div>
          </div>
        </div>
        
        <div className={styles.reviewDivider}></div>
        
        <div className={styles.reviewSection}>
          <h4>Leave Details</h4>
          <div className={styles.reviewGrid}>
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Leave Type</span>
              <span className={styles.reviewValue}>{selectedLeaveTypeInfo?.displayName}</span>
            </div>
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>From</span>
              <span className={styles.reviewValue}>{new Date(startDate).toLocaleDateString('en-GB')}</span>
            </div>
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>To</span>
              <span className={styles.reviewValue}>{new Date(endDate).toLocaleDateString('en-GB')}</span>
            </div>
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Duration</span>
              <span className={styles.reviewValue}>{totalDays} day{totalDays > 1 ? 's' : ''}</span>
            </div>
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Leave Status</span>
              <span className={styles.reviewValue}>{isAlreadyTaken ? 'Already Taken (Past)' : 'Future Leave'}</span>
            </div>
            {reason && (
              <div className={`${styles.reviewItem} ${styles.fullWidth}`}>
                <span className={styles.reviewLabel}>Reason</span>
                <span className={styles.reviewValue}>{reason}</span>
              </div>
            )}
            {certificateFile && (
              <div className={styles.reviewItem}>
                <span className={styles.reviewLabel}>Document</span>
                <span className={styles.reviewValue}>{certificateFile.name}</span>
              </div>
            )}
          </div>
        </div>
        
        {selectedLeaveType === 'emergency' && (
          <div className={styles.warningBox}>
            <strong>Note:</strong> Emergency leave will deduct from both Emergency and Annual leave balances.
          </div>
        )}
        
        <div className={styles.reviewDivider}></div>
        
        <div className={styles.reviewSection}>
          <h4>Engineer Remarks (Optional)</h4>
          <textarea
            value={engineerRemarks}
            onChange={(e) => setEngineerRemarks(e.target.value)}
            placeholder="Add any remarks for HR..."
            rows={2}
            className={styles.formTextarea}
          />
        </div>
        
        <div className={styles.infoBox}>
          This application will be submitted directly to HR Manager for approval.
        </div>
      </div>
      
      <div className={styles.submittedByInfo}>
        <span>Submitted by: <strong>{user?.name || 'Engineer'}</strong></span>
        <span>Employee No: <strong>{user?.employeeNo || user?.uid}</strong></span>
      </div>
      
      <div className={styles.buttonRow}>
        <button onClick={() => setCurrentStep('dates')} className={styles.secondaryButton}>
          Edit Details
        </button>
        <button 
          onClick={handleSubmitApplication}
          disabled={isLoading}
          className={styles.submitButton}
        >
          {isLoading ? 'Submitting...' : 'Submit Application'}
        </button>
      </div>
    </div>
  );
  
  // Step 5: Success
  const renderSuccessStep = () => (
    <div className={styles.stepContainer}>
      <div className={styles.successCard}>
        <div className={styles.successHeader}>
          <h2>Application Submitted Successfully</h2>
          <p>The leave application has been sent to HR Manager for approval.</p>
        </div>
        
        <div className={styles.successDetails}>
          <div className={styles.detailRow}>
            <span>Application ID</span>
            <strong>{submittedApplicationId}</strong>
          </div>
          <div className={styles.detailRow}>
            <span>Employee</span>
            <strong>{selectedEmployee?.employeeName}</strong>
          </div>
          <div className={styles.detailRow}>
            <span>Leave Type</span>
            <strong>{selectedLeaveTypeInfo?.displayName}</strong>
          </div>
          <div className={styles.detailRow}>
            <span>Duration</span>
            <strong>{totalDays} day{totalDays > 1 ? 's' : ''}</strong>
          </div>
          <div className={styles.detailRow}>
            <span>Current Status</span>
            <span className={`${styles.statusLabel} ${styles.statusPending}`}>Pending HR Approval</span>
          </div>
        </div>
        
        <div className={styles.successActions}>
          <button onClick={handleStartOver} className={styles.primaryButton}>
            Apply for Another Employee
          </button>
          <button onClick={() => onNavigate?.('leave-reports')} className={styles.secondaryButton}>
            View All Reports
          </button>
          <button onClick={() => onNavigate?.('dashboard')} className={styles.textButton}>
            Return to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
  
  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  
  return (
    <div className={styles.pageContainer}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <button onClick={() => onNavigate?.('dashboard')} className={styles.backButton}>
            Back to Dashboard
          </button>
        </div>
        <div className={styles.headerCenter}>
          <h1>Leave Application</h1>
          <p>Submit leave request on behalf of workers - Direct to HR Approval</p>
        </div>
        <div className={styles.headerRight}></div>
      </div>
      
      {/* Progress Steps */}
      <div className={styles.progressBar}>
        {[
          { key: 'search', label: 'Search' },
          { key: 'select-leave', label: 'Select Leave' },
          { key: 'dates', label: 'Dates' },
          { key: 'review', label: 'Review' },
          { key: 'success', label: 'Complete' }
        ].map((step, index) => {
          const stepIndex = ['search', 'select-leave', 'dates', 'review', 'success'].indexOf(currentStep);
          const isActive = currentStep === step.key;
          const isCompleted = stepIndex > index;
          
          return (
            <div 
              key={step.key}
              className={`${styles.progressStep} ${isActive ? styles.active : ''} ${isCompleted ? styles.completed : ''}`}
            >
              <div className={styles.stepNumber}>{isCompleted ? '\u2713' : index + 1}</div>
              <span className={styles.stepLabel}>{step.label}</span>
            </div>
          );
        })}
      </div>
      
      {/* Error Display */}
      {error && (
        <div className={styles.errorAlert}>
          <span>{error}</span>
          <button onClick={() => setError(null)} className={styles.closeError}>Close</button>
        </div>
      )}
      
      {/* Step Content */}
      <div className={styles.mainContent}>
        {currentStep === 'search' && renderSearchStep()}
        {currentStep === 'select-leave' && renderSelectLeaveStep()}
        {currentStep === 'dates' && renderDatesStep()}
        {currentStep === 'review' && renderReviewStep()}
        {currentStep === 'success' && renderSuccessStep()}
      </div>
    </div>
  );
};

export default EngineerApplyLeavePage;